/**
 * Review step of the onboarding wizard
 * Displays a summary of the form data before submission
 */
import { makeStyles } from '@material-ui/core';
import {
  DevHubBox,
  DevHubGrid,
  DevHubTypography,
  DevHubAlert,
} from '@devxdiscover/devhub-ui';
import { OnboardingFormData } from '../types';
import {
  formatGitHubRepoName,
  formatCnapTenancyName,
} from '../../utils/resourceFormatting';
import { ResourceLabel } from '../../components';

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  resourcePreviewBox: {
    padding: theme.spacing(2),
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    backgroundColor: '#fafafa',
  },
  resourceItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    backgroundColor: '#fff',
  },
  resourceIcon: {
    marginRight: theme.spacing(1.5),
    width: 20,
    height: 20,
  },
  resourceText: {
    fontFamily: 'monospace',
    fontSize: '0.95rem',
    color: '#666',
  },
}));

interface ReviewStepProps {
  formData: OnboardingFormData;
}

/**
 * Review step component showing form data summary
 * @param props - Component props
 */
export const ReviewStep = ({ formData }: ReviewStepProps) => {
  const classes = useStyles();

  // Generate the repository and tenancy names from the application name
  const repoName = formatGitHubRepoName(formData.name);
  const tenantName = formatCnapTenancyName(formData.name);

  return (
    <DevHubBox>
      {/* Application Details Section */}
      <div className={classes.section}>
        <DevHubTypography
          variant="h6"
          style={{ marginBottom: '12px', fontWeight: 600 }}
        >
          Application Details
        </DevHubTypography>
        <DevHubGrid container spacing={2}>
          <DevHubGrid item xs={3}>
            <DevHubTypography variant="body1" style={{ fontWeight: 500 }}>
              Name
            </DevHubTypography>
          </DevHubGrid>
          <DevHubGrid item xs={9}>
            <DevHubTypography variant="body1">{formData.name}</DevHubTypography>
          </DevHubGrid>
          <DevHubGrid item xs={3}>
            <DevHubTypography variant="body1" style={{ fontWeight: 500 }}>
              Description
            </DevHubTypography>
          </DevHubGrid>
          <DevHubGrid item xs={9}>
            <DevHubTypography variant="body1">
              {formData.description}
            </DevHubTypography>
          </DevHubGrid>
        </DevHubGrid>
      </div>

      {/* Resources to be Created Section */}
      <div className={classes.section}>
        <DevHubTypography
          variant="h6"
          style={{ marginBottom: '12px', fontWeight: 600 }}
        >
          Resources to be Created
        </DevHubTypography>
        <div className={classes.resourcePreviewBox}>
          {/* GitHub Repository */}
          <div className={classes.resourceItem}>
            <img
              src="/icons/github.svg"
              alt="GitHub"
              className={classes.resourceIcon}
            />
            <div style={{ flex: 1 }}>
              <ResourceLabel type="github" style={{ marginBottom: '2px' }}>
                GitHub Repository
              </ResourceLabel>
              <div className={classes.resourceText}>{repoName}</div>
            </div>
          </div>

          {/* CNAP Tenancy */}
          <div className={classes.resourceItem}>
            <img
              src="/icons/cnap.svg"
              alt="CNAP"
              className={classes.resourceIcon}
            />
            <div style={{ flex: 1 }}>
              <ResourceLabel type="cnap" style={{ marginBottom: '2px' }}>
                CNAP Tenancy
              </ResourceLabel>
              <div className={classes.resourceText}>{tenantName}</div>
            </div>
          </div>

          {/* AD Groups */}
          <div className={classes.resourceItem}>
            <img
              src="/icons/azure.svg"
              alt="Azure AD"
              className={classes.resourceIcon}
            />
            <div style={{ flex: 1 }}>
              <ResourceLabel type="adGroups" style={{ marginBottom: '2px' }}>
                Active Directory Groups
              </ResourceLabel>
              <DevHubTypography
                variant="body2"
                style={{ color: '#666', fontSize: '0.95rem' }}
              >
                Groups for admins, users, and collaborators
              </DevHubTypography>
            </div>
          </div>
        </div>
      </div>

      {/* Info Message */}
      <DevHubAlert severity="info">
        <DevHubTypography variant="body2">
          Click <strong>Go</strong> to begin creating these resources. You'll
          see real-time progress as each resource is provisioned.
        </DevHubTypography>
      </DevHubAlert>
    </DevHubBox>
  );
};
